/**
 * 랜딩(WS-00 워크스페이스 홈) 축 카드 선언 — `WORKSPACE_NAV` 파생.
 *
 * 축명·설명·아이콘·대표 route·랜딩 표시값(태그·리드·칩)은 전부 workspaceNav.ts 가 소유한다(075 에서
 * 이중 선언 통합). 이 파일은 그 배열을 **깔때기 배치**(입력 → 분석 → 종합 → 적재)의 카드 형태로 옮긴다.
 * 새 축은 여기가 아니라 WORKSPACE_NAV 에 1행 추가하고, 아래 `AXIS_FLOW` 에 배치 역할만 지정한다.
 *
 * 샘플 데이터는 landingSamples.ts 로 분리했다(서버 조회 없음 — 034 FR-012 유지).
 *
 * ── 배치 모델(`flow`) ──
 * 축을 "N개 중 1개"로 나열하지 않고, 정보가 흐르는 순서로 묶는다. 랜딩의 핵심 메시지가
 * "한 번 입력하면 분석과 리포트가 이어진다"이므로 배치 자체가 그 연결을 보여야 한다.
 *
 *  | flow      | 단계 | 배치                                   |
 *  |-----------|------|----------------------------------------|
 *  | `source`  | 1    | 전폭 리드 — 모든 분석의 재료(내 입시카드) |
 *  | `analysis`| 2    | 병렬(둘 이상이면 2열, 하나면 전폭) — 재료를 소비하는 분석들 |
 *  | `archive` | —    | 하단 바(카드 아님) — 결과 적재 저장소     |
 *
 * `step`(흐름 단계 = 고스트 숫자)과 `tone`(밴드 색 = tokens.css `--step-1~5`)은 **다른 축**이다 —
 * 2단의 병렬 축은 step 을 공유하고 tone 만 가른다. `archive` 축은 위계를 낮춘 바라 둘 다 없다.
 */
import {
  IPSI_CARD_ROUTE,
  VISIBLE_WORKSPACE_NAV,
  type WorkspaceNavIcon,
  type WorkspaceNavKey,
} from './workspaceNav';

/** 축 식별자 — GNB 그룹 key 와 동일(랜딩 전용 축 없음). */
export type LandingAxisKey = WorkspaceNavKey;

/** 축 아이콘 키 — LandingAxisCard 가 lucide-vue-next 컴포넌트로 매핑한다. */
export type LandingAxisIcon = WorkspaceNavIcon;

/** 흐름 단계 — 카드 STEP 배지 숫자. 병렬 축은 같은 값을 공유한다(2단 = 분석 축). */
export type LandingAxisStep = 1 | 2;

/** 좌측 레일·아이콘 색 — `--step-{n}` 토큰 인덱스. 2단 병렬 카드를 서로 가르는 용도. */
export type LandingAxisTone = 1 | 2 | 3 | 4 | 5;

/**
 * 깔때기 내 배치 역할 — 축이 화면 어디에, 어떤 위계로 놓이는지의 단일 선언.
 * 컴포넌트는 이 값으로만 분기한다(key 하드코딩 금지).
 */
export type LandingAxisFlow = 'source' | 'analysis' | 'archive';

/**
 * 축 key → 배치 역할·흐름 단계·강조 색. 새 축 추가 시 여기에 1행.
 *
 * `step`(STEP 배지 숫자)과 `tone`(레일·아이콘 색)을 분리한다 — 2단의 병렬 축은 **같은 단계**(step 2)지만
 * 나란히 놓이므로 색까지 같으면 한 덩어리로 뭉쳐 보인다. 단계는 공유하고 색만 인접 톤으로 가른다.
 *
 * ⚠️ 카드의 **무게**(다크/흰/틴트)는 여기서 정하지 않는다 — `flow` 에서 파생된다(LandingAxisCard 참조).
 *    입구는 무겁게(다크), 갈래는 가볍게(흰). 배치 역할이 곧 시각 위계다.
 */
const AXIS_FLOW: Record<
  LandingAxisKey,
  { flow: LandingAxisFlow; step?: LandingAxisStep; tone?: LandingAxisTone }
> = {
  card: { flow: 'source', step: 1, tone: 1 },
  score: { flow: 'analysis', step: 2, tone: 2 },
  test: { flow: 'analysis', step: 2, tone: 3 },
  reports: { flow: 'archive' },
};

/** 배치 역할을 모르는 신규 축의 기본값 — 분석 열에 합류시켜 렌더가 막히지 않게 한다. */
const FLOW_FALLBACK = { flow: 'analysis', step: 2, tone: 3 } as const;

interface LandingAxisBase {
  key: LandingAxisKey;
  /** 축명(= GNB 대분류 라벨). */
  name: string;
  /** 한 줄 설명(= GNB summary). */
  one: string;
  /** 태그 — 헤더 밴드 칩 텍스트. */
  tag: string;
  icon: LandingAxisIcon;
  /** 깔때기 배치 역할. */
  flow: LandingAxisFlow;
  /** 하위 기능 불릿 — landing.sub 가 없으면 GNB 하위 항목 라벨을 그대로 쓴다. */
  sub?: string[];
  /** 흐름 단계 — 카드 우하단 고스트 숫자. `archive` 는 없음(중성 바). */
  step?: LandingAxisStep;
  /** 헤더 밴드 색 순번. `archive` 는 없음(중성 바). */
  tone?: LandingAxisTone;
}

/**
 * 축 카드 선언 — 불변식(034 data-model §1):
 *  - `status='open'`  ⇒ `route` 필수(클릭 시 이동).
 *  - `status='soon'`  ⇒ `route` 없음("준비중" 표기 + 무동작).
 * 판별 유니언으로 타입 수준에서 강제한다(현재 선언은 전부 open — soon 은 로드맵 축 복귀용 계약).
 */
export type LandingAxis =
  | (LandingAxisBase & { status: 'open'; route: string })
  | (LandingAxisBase & { status: 'soon'; route?: never });

/** 축 카드·샘플 CTA 가 페이지로 올리는 이동 대상 — 게이트 모달의 대상명·복귀 route. */
export interface LandingGoTarget {
  /** 모달 본문에 굵게 찍히는 기능명. */
  name: string;
  route: string;
}

/** 축 카드 — 노출 축(`VISIBLE_WORKSPACE_NAV`) 전부 open. 순서는 GNB 와 같고, 배치는 `flow` 가 가른다. */
export const LANDING_AXES: LandingAxis[] = VISIBLE_WORKSPACE_NAV.map((g): LandingAxis => {
  const { flow, step, tone } = AXIS_FLOW[g.key] ?? FLOW_FALLBACK;
  return {
    key: g.key,
    name: g.label,
    one: g.summary,
    tag: g.landing.tag,
    icon: g.icon,
    sub: g.landing.sub ?? g.items.map((item) => item.label),
    flow,
    step,
    tone,
    status: 'open',
    route: g.route,
  };
});

/** 배치 역할별 축 묶음 — LandingAxes 가 행 단위로 그린다(필터 로직 중복 금지). */
export function axesByFlow(flow: LandingAxisFlow): LandingAxis[] {
  return LANDING_AXES.filter((axis) => axis.flow === flow);
}

/**
 * [시작하기] 비로그인 복귀지 — 로그인 시점에는 성적 보유 여부를 알 수 없으므로 허브(입시카드) 고정.
 * 로그인 회원의 실제 이동 대상은 성적 보유 여부로 갈린다 — AnalysisHomePage 의 분기 참조(035 FR-022).
 */
export const LANDING_START_ROUTE = IPSI_CARD_ROUTE;
